import { useContext, useMemo, useState, type ReactNode } from "react";
import { describe, pickable, type Link, type Selection } from "@/artifacts/links";
import { CloseIcon } from "@/components/icons";
import { cn } from "@/lib/cn";
import {
  ScopeContext,
  type LinkedArtifact,
  type Scope,
  type ViewQuery,
  type ZoomQuery,
} from "./linked";

/**
 * One answer's artifacts, answering to one selection (LINK-2).
 *
 * Click California on the map and the county table narrows to California's
 * counties; the state ranking keeps every bar and lights California's. The
 * server published which artifacts share what and how each reacts; this holds
 * the reader's current selection and hands each card what it needs.
 *
 * A selection is a reader's view, exactly like the bar chart's sort toggle:
 * never saved, never printed, gone on reload. With nothing selected every
 * card renders exactly as it would outside a scope.
 */

export function LinkedScope({
  links,
  view,
  drill,
  zoom,
  onAsk,
  label = "Filter this answer",
  barClassName,
  children,
  className,
}: {
  links: readonly Link[];
  /** What the filter bar calls the scope: "Filter this answer", "… report". */
  label?: string;
  /** Spacing for the bar where its container does not provide any. */
  barClassName?: string;
  view?: ViewQuery;
  drill?: ViewQuery;
  zoom?: ZoomQuery;
  onAsk?: (text: string) => void;
  children: ReactNode;
  className?: string;
}) {
  const [selection, setSelection] = useState<Selection | null>(null);
  const scope = useMemo<Scope>(
    () => ({
      links,
      selection,
      select: setSelection,
      view: view ?? null,
      drill: drill ?? null,
      zoom: zoom ?? null,
      onAsk: onAsk ?? null,
    }),
    [links, selection, view, drill, zoom, onAsk],
  );
  if (!links.length) return <div className={className}>{children}</div>;
  return (
    <ScopeContext.Provider value={scope}>
      <div
        className={className}
        onKeyDown={(event) => {
          if (event.key === "Escape" && selection) {
            event.stopPropagation();
            setSelection(null);
          }
        }}
      >
        <FilterBar label={label} className={barClassName} />
        {children}
      </div>
    </ScopeContext.Provider>
  );
}

/** The selection made without a pointer, and the one place that says what is selected. */
function FilterBar({ label, className }: { label: string; className?: string }) {
  const scope = useContext(ScopeContext);
  if (!scope) return null;
  const { links, selection, select, onAsk } = scope;
  const active = selection ? links.find((link) => link.id === selection.link) : undefined;
  const said = active && selection ? describe(active, selection) : "";

  return (
    // Sticky: a reader scrolled down to the county table is the one who most
    // needs to see what it is filtered to, and how to undo it.
    // Hidden on paper: a printed page never carries a selection, even one
    // printed from an exported file by the reader's own browser.
    <div
      className={cn(
        "border-border bg-surface rounded-element shadow-card sticky top-0 z-20 flex flex-wrap items-center gap-x-3 gap-y-2 border px-3 py-2 print:hidden",
        className,
      )}
    >
      <span className="type-caption text-ink-secondary">{label}</span>
      {links.map((link) => {
        const { grain, members } = pickable(link);
        const picked =
          selection?.link === link.id && selection.grain === grain && selection.keys.length === 1
            ? selection.keys[0]
            : "";
        return (
          <label key={link.id} className="flex items-center gap-1.5">
            <span className="type-caption text-ink-secondary">{link.label}</span>
            <select
              // Named outright: a wrapping label would fold every option's text
              // into the name a screen reader announces.
              aria-label={`Filter by ${link.label.toLowerCase()}`}
              value={picked}
              onChange={(event) =>
                select(
                  event.target.value
                    ? { link: link.id, grain, keys: [event.target.value], source: -1 }
                    : null,
                )
              }
              className="type-support text-ink bg-card border-border rounded-inner max-w-44 border px-1.5 py-0.5"
            >
              <option value="">All</option>
              {members.map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
        );
      })}
      {active && selection ? (
        <span className="bg-accent-subtle text-ink rounded-inner type-support inline-flex items-center gap-1 py-0.5 ps-2 pe-0.5">
          Showing {said}
          <button
            type="button"
            aria-label="Clear the filter"
            title="Clear (Esc)"
            onClick={() => select(null)}
            className="hover:bg-hover rounded-inner motion-fast inline-flex size-5 items-center justify-center"
          >
            <CloseIcon className="size-3" />
          </button>
        </span>
      ) : null}
      {said && onAsk ? (
        // Going deeper than the answer's rows is a new question, and a model
        // is never spent without a person choosing to: this only writes it.
        <button
          type="button"
          onClick={() => onAsk(`Break ${said} down one level further.`)}
          className="type-support text-accent motion-fast hover:underline"
        >
          Ask about {said} →
        </button>
      ) : null}
      {/* Always mounted, so the first announcement is heard (backlog item 11). */}
      <span aria-live="polite" className="sr-only">
        {said ? `Showing ${said}` : ""}
      </span>
    </div>
  );
}

/** A chip for a card's header: what it follows, and whether it is following now. */
export function FollowsChip({ linked }: { linked: LinkedArtifact }) {
  if (!linked.follows.length) return null;
  const reacting = linked.reaction !== null;
  return (
    <span
      title="Selecting in a linked artifact updates this one"
      className={cn(
        "type-caption rounded-inner px-1.5 py-0.5 normal-case",
        reacting ? "bg-accent-subtle text-ink" : "text-ink-disabled",
      )}
    >
      Follows {linked.follows.join(" · ").toLowerCase()}
    </span>
  );
}
